import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { MatchResultsList } from "./MatchResultsList";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

interface FindMatchesModalProps {
	isOpen: boolean;
	onClose: () => void;
	jobId: string | undefined;
	jobSkills?: string[];
	onAddMatches?: (matches: any[]) => void;
}

export function FindMatchesModal({
	isOpen,
	onClose,
	jobId,
	jobSkills = [],
	onAddMatches,
}: FindMatchesModalProps) {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [matchResults, setMatchResults] = useState<any>(null);
	const [scoreThreshold, setScoreThreshold] = useState(70);

	const findMatches = async () => {
		if (!jobId) return;

		setIsLoading(true);
		try {
			console.log("Invoking quick-match function with jobId:", jobId);
			const { data, error } = await supabase.functions.invoke("quick-match", {
				body: { jobId }
			});

			if (error) {
				console.error("Error finding matches:", error);
				throw error;
			}

			console.log("Received match results:", data);
			setMatchResults(data.results);
		} catch (error) {
			console.error("Error finding matches:", error);
			toast({
				title: t("errors.error"),
				description: t("jobs.matches.findMatchesError"),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (isOpen && jobId) {
			findMatches();
		}
		return () => {
			if (!isOpen) {
				setMatchResults(null);
			}
		};
	}, [isOpen, jobId]);

	const handleAddSelected = (selectedCandidates: string[]) => {
		onAddMatches?.(selectedCandidates);
		onClose();
	};

	const handleAddAll = () => {
		// Get all candidates that meet the current threshold
		const filteredResults = matchResults 
			? Object.values(matchResults)
				.filter((result: any) => result.scorePercent * 100 >= scoreThreshold)
				.map((result: any) => result.id)
			: [];
			
		onAddMatches?.(filteredResults);
		onClose();
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="w-[90vw] max-w-4xl h-[85vh] p-0 gap-0 overflow-hidden">
				<div className="flex flex-col h-full relative">
					<DialogHeader className="p-6 border-b sticky top-0 bg-white z-10">
						<DialogTitle>{t("jobs.matches.potentialMatches")}</DialogTitle>
						<div className="mt-4 px-4">
							<div className="flex items-center gap-4">
								<span className="text-sm text-gray-500 min-w-[90px]">{t("jobs.matches.matchScore")}:</span>
								<Slider
									value={[scoreThreshold]}
									onValueChange={(value) => setScoreThreshold(value[0])}
									min={0}
									max={100}
									step={1}
									className={`flex-1 ${scoreThreshold < 35 ? '[&>span]:bg-red-500' : '[&>span]:bg-digger-blue'}`}
								/>
								<span className="text-sm font-medium min-w-[40px]">{scoreThreshold}%</span>
							</div>
						</div>
					</DialogHeader>
					<div className="flex-1 p-6 pb-0 overflow-hidden">
						<MatchResultsList 
							results={matchResults || {}} 
							isLoading={isLoading}
							scoreThreshold={scoreThreshold}
							jobId={jobId}
							onAddSelected={handleAddSelected}
							onAddAll={handleAddAll}
						/>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
